import React, { useRef } from 'react';
import { connect } from 'react-redux';
import { History, Location } from 'history';
import { Formik, FormikProps } from 'formik';
import cn from 'classnames';

import { IRootState } from 'src/redux/rootReducer';
import { Input, View, Button, Text, Image, NavLink } from 'src/components/common';
import Form from 'src/components/common/Form';
import Footer from 'src/components/Footer';

import { IMAGES } from 'src/appConfig/images';
import { PATHS } from 'src/appConfig/paths';
import { getLocationState } from 'src/utils';
import { forgotPasswordAsync } from 'src/redux/auth/actions';
import { useComponentDidMount } from 'src/hooks';
import { Yup } from 'src/services';

type FormValue = {
  email: string;
  isFirstTimeLogin?: boolean;
};

const INITIAL = {
  email: '',
  isFirstTimeLogin: false,
};

const ForgotPassword: React.FC<Props> = ({ error, loading, location, onForgotPassword }) => {
  const formRef = useRef<FormikProps<FormValue>>(null);
  useComponentDidMount(() => {
    const state = getLocationState(location);
    if (state?.email) {
      formRef.current.setFieldValue('email', state.email as string);
    }
    if (state?.action) {
      formRef.current.setFieldValue('isFirstTimeLogin', !!state.action ? true : false);
    }
  });

  // =========================== FORGOT PASSWORD ===========================
  const handleSubmitForgotPassword = (values: FormValue) => {
    const { email, isFirstTimeLogin } = values;

    // console.log(values);

    onForgotPassword({ email, isFirstTimeLogin });
  };

  // =========================== SCHEMA ===========================
  const ForgotPasswordSchema = Yup.object().shape({
    email: Yup.string().required().email(),
  });

  return (
    <View className="ctn-uam" flexGrow={1}>
      <Image className="ctn-uam__image" src={IMAGES.backgroundLogin} />
      <View className="container" flexGrow={1}>
        <View className="ctn-uam__container" flexGrow={1}>
          <Formik
            initialValues={INITIAL}
            onSubmit={handleSubmitForgotPassword}
            validationSchema={ForgotPasswordSchema}
            innerRef={formRef}>
            {({ values, errors, touched, getFieldProps, handleSubmit }) => {
              // console.log(values);

              return (
                <Form onSubmit={handleSubmit} autoComplete="off" className="ctn-uam__form">
                  <h1 className={cn('ctn-uam__title mb-8')}>
                    {values.isFirstTimeLogin ? 'Welcome!' : 'Forgot Password'}
                  </h1>
                  <Text className={cn('mb-48')}>
                    {values.isFirstTimeLogin ? (
                      <span>
                        Please enter your email address you use to log in.
                        <br />A verification code will be sent to this email.
                      </span>
                    ) : (
                      'Enter your registered email to recover your password.'
                    )}
                  </Text>

                  <Input
                    label="Email"
                    placeholder="Email"
                    errorMessage={touched.email ? errors.email : ''}
                    containerClassName="mb-40"
                    {...getFieldProps('email')}
                  />

                  <Button className="mb-32" isLoading={loading}>
                    CONTINUE
                  </Button>
                </Form>
              );
            }}
          </Formik>

          <Text className="mb-20" size={14}>
            Already had account? <NavLink to={PATHS.signIn}>LOG IN</NavLink>
          </Text>

          <Footer className="ctn-uam__footer" />
        </View>
      </View>
    </View>
  );
};

type Props = ReturnType<typeof mapStateToProps> &
  typeof mapDispatchToProps & { history: History; location: Location<string> };

const mapStateToProps = (state: IRootState) => ({
  loading: state.auth.loading,
  error: state.auth.error,
});

const mapDispatchToProps = {
  onForgotPassword: forgotPasswordAsync.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(ForgotPassword);
